import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoArrowBack,
  IoPersonCircle,
  IoCheckmark,
  IoChevronForward,
  IoPersonAdd,
  IoPhonePortraitOutline,
  IoChatbubble,
  IoLogoApple,
} from 'react-icons/io5';
import { FcGoogle } from 'react-icons/fc';
import { signOut } from 'firebase/auth';
import { auth } from '../../../firebase/config';

const PROVIDER_META = {
  kakao: { label: '카카오 계정', bg: '#FEE500' },
  google: { label: '구글 계정', bg: '#FFFFFF', border: '#E5E7EB' },
  apple: { label: '애플 계정', bg: '#000000' },
};

const ProviderIcon = ({ provider }) => {
  if (provider === 'kakao') return <IoChatbubble size={20} color="#000" />;
  if (provider === 'apple') return <IoLogoApple size={20} color="#fff" />;
  return <FcGoogle size={20} />;
};

const NoAccountPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { authUid, provider, email, displayName, phone, verifiedToken } = state;

  useEffect(() => {
    if (!authUid || !provider) {
      navigate('/login', { replace: true });
    }
  }, [authUid, provider, navigate]);

  if (!authUid || !provider) return null;

  const goSignup = () => {
    navigate('/social/role-select', {
      state: { authUid, email, displayName, provider, phone, verifiedToken },
    });
  };

  const goReverify = () => {
    navigate('/social/link-phone', {
      state: { authUid, email, displayName, provider },
      replace: true,
    });
  };

  const goLogin = async () => {
    try { await signOut(auth); } catch {}
    navigate('/login', { replace: true });
  };

  return (
    <Wrapper>
      <Card>
        <BackBtn onClick={() => navigate(-1)}>
          <IoArrowBack size={16} />
          <span>뒤로가기</span>
        </BackBtn>

        <IconCircle>
          <IoPersonCircle size={28} color="#22C55E" />
        </IconCircle>

        <Title>기존 계정이 없습니다</Title>
        <SubTitle>
          <strong>인증된 번호</strong>로 가입된 이음 계정을 찾을 수 없었습니다.
        </SubTitle>

        <Divider />

        <SocialBox>
          <SmallLabel>로그인 시도한 소셜 계정</SmallLabel>
          <SocialRow>
            <Avatar $provider={provider}>
              <ProviderIcon provider={provider} />
            </Avatar>
            <div>
              <AccName>{PROVIDER_META[provider]?.label || '소셜 계정'}</AccName>
              <AccDesc>인증된 번호</AccDesc>
            </div>
          </SocialRow>
        </SocialBox>

        <InfoBox>
          <InfoHeader>
            <span>안내</span>
          </InfoHeader>
          <InfoItem>
            <IoCheckmark size={14} />
            <span>해당 휴대폰 번호로 가입된 이음 계정이 없습니다.</span>
          </InfoItem>
          <InfoItem>
            <IoCheckmark size={14} />
            <span>신규 회원가입을 진행하거나, 다른 방법으로 로그인해 주세요.</span>
          </InfoItem>
          <InfoItem>
            <IoCheckmark size={14} />
            <span>이미 다른 번호로 가입하셨다면 해당 번호로 다시 인증해 주세요.</span>
          </InfoItem>
        </InfoBox>

        <ActionCard $primary onClick={goSignup}>
          <ActionIcon $primary>
            <IoPersonAdd size={18} color="#fff" />
          </ActionIcon>
          <ActionBody>
            <ActionName $primary>신규 회원가입</ActionName>
            <ActionDesc $primary>{PROVIDER_META[provider]?.label || '소셜 계정'}으로 새로 가입합니다</ActionDesc>
          </ActionBody>
          <IoChevronForward size={18} color="#fff" />
        </ActionCard>

        <ActionCard onClick={goReverify}>
          <ActionIcon>
            <IoPhonePortraitOutline size={18} />
          </ActionIcon>
          <ActionBody>
            <ActionName>다른 번호로 다시 인증</ActionName>
            <ActionDesc>다른 휴대폰 번호로 기존 계정을 찾습니다</ActionDesc>
          </ActionBody>
          <IoChevronForward size={18} color="#9CA3AF" />
        </ActionCard>

        <BackToLogin onClick={goLogin}>로그인 화면으로 돌아가기</BackToLogin>
      </Card>
    </Wrapper>
  );
};

export default NoAccountPage;

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.bg};

  @media (min-width: 1024px) {
    align-items: center;
    background: #2f3542;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xl};

  @media (min-width: 1024px) {
    padding: 40px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  }
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #DCFCE7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const SubTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};

  strong { color: ${({ theme }) => theme.colors.text}; font-weight: ${({ theme }) => theme.fontWeight.semibold}; }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SocialBox = styled.div`
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const SmallLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $provider }) => PROVIDER_META[$provider]?.bg || '#EFF4FF'};
  border: 1px solid ${({ $provider }) => PROVIDER_META[$provider]?.border || 'transparent'};
`;

const AccName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const AccDesc = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const InfoBox = styled.div`
  padding: 14px 16px;
  background: #EFF4FF;
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const InfoHeader = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #4A6CF7;
  margin-bottom: 6px;

  svg { flex-shrink: 0; margin-top: 3px; }
`;

const ActionCard = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: 16px;
  border: 1px solid ${({ $primary, theme }) => ($primary ? theme.colors.primary : theme.colors.border)};
  background: ${({ $primary, theme }) => ($primary ? theme.colors.primary : '#fff')};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  text-align: left;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  &:hover { opacity: 0.95; }
`;

const ActionIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $primary }) => ($primary ? 'rgba(255,255,255,0.2)' : '#F3F4F6')};
  color: ${({ $primary, theme }) => ($primary ? '#fff' : theme.colors.textSecondary)};
  flex-shrink: 0;
`;

const ActionBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActionName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ $primary, theme }) => ($primary ? '#fff' : theme.colors.text)};
`;

const ActionDesc = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ $primary, theme }) => ($primary ? 'rgba(255,255,255,0.85)' : theme.colors.textSecondary)};
`;

const BackToLogin = styled.button`
  display: block;
  margin: ${({ theme }) => theme.spacing.sm} auto 0;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;

  &:hover { color: ${({ theme }) => theme.colors.text}; }
`;
